import { useState, useEffect, useCallback } from 'react';
import {
  Loader,
  Plus,
  Pin,
  PinOff,
  Pencil,
  Trash2,
  FileText,
  AlertCircle,
} from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { PartnershipNote } from '../../types/partnership';
import { partnershipsApiService } from '../../services/partnershipsApi';
import { usernameCacheService } from '../../services/usernameCache';
import { cn } from '../../utils/cn';
import { MarkdownRenderer } from '../MarkdownRenderer';
import MarkdownEditor from '../MarkdownEditor';
import { CreatorProfile } from '../CreatorProfile';
import { SectionHeader } from '../ui/SectionHeader';
import { EmptyState } from '../ui/EmptyState';
import { SkeletonLoader } from '../SkeletonLoader';

const TRUNCATE_LENGTH = 200;

interface PartnershipNotesSectionProps {
  partnershipId: string;
  canCreate: boolean;
  canEdit: boolean;
  canDelete: boolean;
  onUpdate: () => void;
}

export const PartnershipNotesSection = ({
  partnershipId,
  canCreate,
  canEdit,
  canDelete,
  onUpdate,
}: PartnershipNotesSectionProps) => {
  const [notes, setNotes] = useState<PartnershipNote[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingNoteId, setEditingNoteId] = useState<string | null>(null);
  const [expandedNoteIds, setExpandedNoteIds] = useState<Set<string>>(new Set());

  // Create form state
  const [createTitle, setCreateTitle] = useState('');
  const [createContent, setCreateContent] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Edit form state (per note)
  const [editTitle, setEditTitle] = useState('');
  const [editContent, setEditContent] = useState('');
  const [isUpdating, setIsUpdating] = useState(false);

  const loadNotes = useCallback(async () => {
    if (!partnershipId) return;
    setLoading(true);
    setError(null);
    try {
      const data = await partnershipsApiService.getNotesForPartnership(partnershipId);
      setNotes(data);

      // Prefetch author UUIDs
      const authorIds = data.map((n) => n.author_id).filter(Boolean);
      if (authorIds.length > 0) {
        usernameCacheService.prefetchUsernames(authorIds);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load notes');
    } finally {
      setLoading(false);
    }
  }, [partnershipId]);

  useEffect(() => {
    loadNotes();
  }, [loadNotes]);

  const sortedNotes = [...notes].sort((a, b) => {
    if (a.pinned !== b.pinned) return b.pinned - a.pinned;
    return b.created_at - a.created_at;
  });

  const handleCreate = async () => {
    if (!createContent.trim()) return;
    setIsSubmitting(true);
    try {
      await partnershipsApiService.createNote(partnershipId, {
        title: createTitle.trim() || undefined,
        content: createContent.trim(),
      });
      setCreateTitle('');
      setCreateContent('');
      setShowCreateForm(false);
      await loadNotes();
      onUpdate();
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to create note');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleUpdateNote = async (noteId: string) => {
    setIsUpdating(true);
    try {
      await partnershipsApiService.updateNote(noteId, {
        title: editTitle.trim() || null,
        content: editContent.trim(),
      });
      setEditingNoteId(null);
      setEditTitle('');
      setEditContent('');
      await loadNotes();
      onUpdate();
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to update note');
    } finally {
      setIsUpdating(false);
    }
  };

  const handlePin = async (noteId: string, pinned: boolean) => {
    try {
      await partnershipsApiService.pinNote(noteId, pinned);
      await loadNotes();
      onUpdate();
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to pin/unpin note');
    }
  };

  const handleDelete = async (noteId: string) => {
    if (!window.confirm('Are you sure you want to delete this note?')) return;
    try {
      await partnershipsApiService.deleteNote(noteId);
      await loadNotes();
      onUpdate();
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to delete note');
    }
  };

  const startEdit = (note: PartnershipNote) => {
    setEditingNoteId(note.id);
    setEditTitle(note.title || '');
    setEditContent(note.content);
  };

  const cancelEdit = () => {
    setEditingNoteId(null);
    setEditTitle('');
    setEditContent('');
  };

  const toggleExpand = (noteId: string) => {
    setExpandedNoteIds((prev) => {
      const next = new Set(prev);
      if (next.has(noteId)) next.delete(noteId);
      else next.add(noteId);
      return next;
    });
  };

  if (loading) {
    return (
      <div className="space-y-4" aria-busy="true">
        <div className="flex items-center justify-between">
          <SkeletonLoader className="h-4 w-24" />
          <SkeletonLoader className="h-9 w-24 rounded-lg" />
        </div>
        {Array.from({ length: 3 }).map((_, i) => (
          <div key={i} className="subcard p-4 space-y-2">
            <div className="flex items-center gap-2">
              <SkeletonLoader className="h-4 w-40" />
              <SkeletonLoader className="h-3 w-20" />
            </div>
            <SkeletonLoader className="h-3 w-full" />
            <SkeletonLoader className="h-3 w-3/4" />
          </div>
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <EmptyState
        bare
        compact
        tone="danger"
        icon={AlertCircle}
        title="Failed to load notes"
        description={error}
        action={<button type="button" onClick={loadNotes} className="glass-button">Retry</button>}
      />
    );
  }

  return (
    <div className="space-y-4">
      {/* Header + Add */}
      <SectionHeader
        title="Notes"
        icon={FileText}
        count={notes.length}
        actions={
          canCreate && !showCreateForm ? (
            <button
              type="button"
              onClick={() => setShowCreateForm(true)}
              className="glass-button"
            >
              <Plus className="w-4 h-4" />
              Add Note
            </button>
          ) : undefined
        }
      />

      {/* Create form */}
      {showCreateForm && (
        <div className="subcard p-4 space-y-3">
          <div className="eyebrow">New Note</div>
          <input
            type="text"
            placeholder="Title (optional)"
            value={createTitle}
            onChange={(e) => setCreateTitle(e.target.value)}
            className="glass-input w-full"
          />
          <MarkdownEditor
            value={createContent}
            onChange={setCreateContent}
            height={200}
            placeholder="Write your note..."
          />
          <div className="flex items-center justify-end gap-2">
            <button
              type="button"
              onClick={() => {
                setShowCreateForm(false);
                setCreateTitle('');
                setCreateContent('');
              }}
              className="glass-button-secondary"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleCreate}
              disabled={!createContent.trim() || isSubmitting}
              className="btn-primary min-w-[6rem]"
            >
              {isSubmitting ? (
                <Loader className="w-4 h-4 animate-spin" />
              ) : (
                'Submit'
              )}
            </button>
          </div>
        </div>
      )}

      {/* Notes list */}
      {sortedNotes.length === 0 && !showCreateForm ? (
        <EmptyState
          bare
          compact
          icon={FileText}
          title="No notes yet"
          description="Internal notes about this partnership will appear here."
        />
      ) : (
        <div className="space-y-3">
          {sortedNotes.map((note) => (
            <div key={note.id} className={cn('subcard p-4', note.pinned === 1 && 'border-amber-500/25')}>
              {editingNoteId === note.id ? (
                <>
                  <div className="eyebrow mb-3">Edit Note</div>
                  <input
                    type="text"
                    placeholder="Title (optional)"
                    value={editTitle}
                    onChange={(e) => setEditTitle(e.target.value)}
                    className="glass-input w-full mb-3"
                  />
                  <MarkdownEditor
                    value={editContent}
                    onChange={setEditContent}
                    height={200}
                    placeholder="Write your note..."
                  />
                  <div className="flex items-center justify-end gap-2 mt-3">
                    <button type="button" onClick={cancelEdit} className="glass-button-secondary">
                      Cancel
                    </button>
                    <button
                      type="button"
                      onClick={() => handleUpdateNote(note.id)}
                      disabled={!editContent.trim() || isUpdating}
                      className="btn-primary min-w-[5rem]"
                    >
                      {isUpdating ? (
                        <Loader className="w-4 h-4 animate-spin" />
                      ) : (
                        'Save'
                      )}
                    </button>
                  </div>
                </>
              ) : (
                <>
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        {note.pinned === 1 && (
                          <span className="text-amber-400" title="Pinned">
                            <Pin className="w-3.5 h-3.5" />
                          </span>
                        )}
                        {note.title && (
                          <span className="text-sm font-semibold text-white">{note.title}</span>
                        )}
                        <CreatorProfile creatorId={note.author_id} inline />
                        <span className="text-xs text-glass-500 tabular-nums">
                          {formatDistanceToNow(new Date(note.created_at), {
                            addSuffix: true,
                          })}
                        </span>
                      </div>

                      <div className="mt-2 text-sm text-glass-300">
                        {note.content.length > TRUNCATE_LENGTH &&
                        !expandedNoteIds.has(note.id) ? (
                          <>
                            <MarkdownRenderer
                              content={note.content.slice(0, TRUNCATE_LENGTH) + '...'}
                              className="text-sm"
                            />
                            <button
                              type="button"
                              onClick={() => toggleExpand(note.id)}
                              className="link text-xs mt-1"
                            >
                              Show more
                            </button>
                          </>
                        ) : note.content.length > TRUNCATE_LENGTH ? (
                          <>
                            <MarkdownRenderer content={note.content} className="text-sm" />
                            <button
                              type="button"
                              onClick={() => toggleExpand(note.id)}
                              className="link text-xs mt-1"
                            >
                              Show less
                            </button>
                          </>
                        ) : (
                          <MarkdownRenderer content={note.content} className="text-sm" />
                        )}
                      </div>
                    </div>

                    <div className="flex items-center gap-0.5 flex-shrink-0">
                      {canEdit && (
                        <>
                          <button
                            type="button"
                            onClick={() => handlePin(note.id, note.pinned === 1 ? false : true)}
                            className={cn(
                              'btn-icon btn-icon-sm',
                              note.pinned === 1 && 'text-amber-400 hover:text-amber-300'
                            )}
                            title={note.pinned === 1 ? 'Unpin' : 'Pin'}
                            aria-label={note.pinned === 1 ? 'Unpin note' : 'Pin note'}
                          >
                            {note.pinned === 1 ? (
                              <PinOff className="w-4 h-4" />
                            ) : (
                              <Pin className="w-4 h-4" />
                            )}
                          </button>
                          <button
                            type="button"
                            onClick={() => startEdit(note)}
                            className="btn-icon btn-icon-sm"
                            title="Edit"
                            aria-label="Edit note"
                          >
                            <Pencil className="w-4 h-4" />
                          </button>
                        </>
                      )}
                      {canDelete && (
                        <button
                          type="button"
                          onClick={() => handleDelete(note.id)}
                          className="btn-icon btn-icon-sm text-red-400 hover:text-red-300"
                          title="Delete"
                          aria-label="Delete note"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  </div>
                </>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
